import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SiteSettings {
  site_title: string;
  site_description: string;
  meta_keywords?: string;
  canonical_url?: string;
  og_title?: string;
  og_description?: string;
  og_image_path?: string;
  og_type: string;
  og_locale: string;
  twitter_card: string;
  twitter_site?: string;
  twitter_creator?: string;
  twitter_image_path?: string;
  favicon_path?: string;
  google_analytics_id?: string;
  google_tag_manager_id?: string;
  facebook_pixel_id?: string;
  schema_type: string;
  schema_data?: any;
}

const SEOHead = () => {
  const getImageUrl = (path: string) => {
    if (!path) return null;
    const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
    return data.publicUrl;
  };

  useEffect(() => {
    const updateSEO = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('*')
        .limit(1)
        .single();

      if (data) {
        // Update title
        document.title = data.site_title;

        // Update meta description
        updateMetaTag('name', 'description', data.site_description);
        
        // Update meta keywords
        if (data.meta_keywords) {
          updateMetaTag('name', 'keywords', data.meta_keywords);
        }
        
        // Update canonical URL
        if (data.canonical_url) {
          updateCanonicalUrl(data.canonical_url);
        }

        // Update Open Graph tags
        updateMetaTag('property', 'og:title', data.og_title || data.site_title);
        updateMetaTag('property', 'og:description', data.og_description || data.site_description);
        updateMetaTag('property', 'og:type', data.og_type);
        updateMetaTag('property', 'og:locale', data.og_locale);
        
        if (data.og_image_path) {
          const ogImageUrl = getImageUrl(data.og_image_path);
          if (ogImageUrl) {
            updateMetaTag('property', 'og:image', ogImageUrl);
          }
        }

        // Update Twitter tags - use Open Graph data as fallback
        updateMetaTag('name', 'twitter:card', data.twitter_card);
        updateMetaTag('name', 'twitter:title', data.og_title || data.site_title);
        updateMetaTag('name', 'twitter:description', data.og_description || data.site_description);
        
        // Use Open Graph image if no specific Twitter image is set
        const twitterImagePath = data.twitter_image_path || data.og_image_path;
        if (twitterImagePath) {
          const twitterImageUrl = getImageUrl(twitterImagePath);
          if (twitterImageUrl) {
            updateMetaTag('name', 'twitter:image', twitterImageUrl);
          }
        }
        
        if (data.twitter_site) {
          updateMetaTag('name', 'twitter:site', data.twitter_site);
        }
        if (data.twitter_creator) {
          updateMetaTag('name', 'twitter:creator', data.twitter_creator);
        }

        // Update favicon
        if (data.favicon_path) {
          const faviconUrl = getImageUrl(data.favicon_path);
          if (faviconUrl) {
            updateFavicon(faviconUrl);
          }
        }
        
        // Add Google Analytics
        if (data.google_analytics_id) {
          addGoogleAnalytics(data.google_analytics_id);
        }
        
        // Add Google Tag Manager
        if (data.google_tag_manager_id) {
          addGoogleTagManager(data.google_tag_manager_id);
        }
        
        // Add Facebook Pixel
        if (data.facebook_pixel_id) {
          addFacebookPixel(data.facebook_pixel_id);
        }
        
        // Add structured data
        if (data.schema_data) {
          addStructuredData(data.schema_data);
        }
      }
    };

    updateSEO();
  }, []);

  return null;
};

const updateMetaTag = (attribute: string, name: string, content: string) => {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const updateCanonicalUrl = (url: string) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.setAttribute('href', url);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
  }
};

const addGoogleAnalytics = (trackingId: string) => {
  // Remove existing GA scripts
  const existingScripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag"]');
  existingScripts.forEach(script => script.remove());
  
  // Add new GA script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script1);
  
  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${trackingId}');
  `;
  document.head.appendChild(script2);
};

const addGoogleTagManager = (containerId: string) => {
  // Add GTM script to head
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.appendChild(script);
  
  // Add GTM noscript to body
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);
};

const addFacebookPixel = (pixelId: string) => {
  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);
};

const addStructuredData = (schemaData: any) => {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.innerHTML = JSON.stringify(schemaData);
  document.head.appendChild(script);
};

const updateFavicon = (faviconUrl: string) => {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach(link => link.remove());

  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = faviconUrl;
  link.type = 'image/png';
  document.head.appendChild(link);
};

export default SEOHead;